import { useEffect, useRef, useState } from 'react'
import { Box, Flex, Text, VStack, Grid, Input, Spinner, useT } from '@minetrace/ui'
import {
  LuPlus as Plus,
  LuTrash2 as Trash2,
  LuCircleAlert as AlertCircle,
} from 'react-icons/lu'
import { useForm, Controller, useFormColors } from '@minetrace/ui/forms'
import { FormProvider } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { toaster } from '@minetrace/ui'
import { AdvancedMarker } from '@vis.gl/react-google-maps'
import { KMap, FitBounds, MarkerTooltip, useMap } from '../lib/googleMaps'
import { ComponentPin } from '../lib/mapMarkers'
import { utmToLatLng, isValidLatLng } from '../utils/utmConverter'
import CharCounter from './CharCounter'

type LL = google.maps.LatLngLiteral

/**
 * Badge numérico del pin: índice 1-based del componente, el mismo número que
 * aparece en la card de la izquierda para emparejar lista ↔ mapa de un
 * vistazo. Rojo cuando el componente cae fuera del polígono del D.M.
 */
function NumberBadge({ label, outside }: { label: number; outside: boolean }) {
  return (
    <span style={outside ? { background: '#b23a26', color: 'white', display: 'block', margin: '0 -3px', padding: '0 3px', borderRadius: 8 } : undefined}>
      {label}
    </span>
  )
}

/**
 * Polígono imperativo sobre `google.maps.Polygon` (la base solo trae Polyline).
 * Mismo patrón de ciclo de vida que `lib/googleMaps/Polyline`.
 */
function Polygon({ paths }: { paths: LL[] }) {
  const map = useMap()
  const ref = useRef<google.maps.Polygon | null>(null)
  useEffect(() => {
    if (!map) return
    const poly = new google.maps.Polygon()
    poly.setMap(map)
    ref.current = poly
    return () => {
      poly.setMap(null)
      ref.current = null
    }
  }, [map])
  useEffect(() => {
    ref.current?.setOptions({
      paths,
      fillColor: '#1d6664',
      fillOpacity: 0.1,
      strokeColor: '#1d6664',
      strokeWeight: 2,
      clickable: false,
    })
  }, [paths])
  return null
}

/**
 * Centra el mapa en el componente cuyo número se clicó en la lista (equivalente
 * al `map.flyTo` de Leaflet). Reacciona al `nonce` para repetir el vuelo aunque
 * se vuelva a clicar la misma card.
 */
function FlyToFocus({ focus }: { focus: { lat: number; lng: number; nonce: number } | null }) {
  const map = useMap()
  useEffect(() => {
    if (!map || !focus) return
    map.panTo({ lat: focus.lat, lng: focus.lng })
    map.setZoom(17)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focus?.nonce])
  return null
}

export interface ComponentEditorItem {
  id: string
  componentTypeId: string
  componentTypeName?: string | null
  componentTypeCode?: string | null
  name?: string | null
  northing: number
  easting: number
  isOutsidePolygon: boolean
}

export interface ComponentTypeOption {
  value: string
  label: string
  tierCode?: string | null
  tierName?: string | null
  description?: string | null
}

// Group type options by tier (Principal / Secundario) for the <optgroup>
// rendering, preserving the incoming order (already sorted by criticality).
const groupTypesByTier = (types: ComponentTypeOption[]) => {
  const groups: { key: string; label: string; items: ComponentTypeOption[] }[] = []
  for (const t of types) {
    const key = t.tierCode ?? '_'
    const label = t.tierName ?? 'Otros'
    let g = groups.find((x) => x.key === key)
    if (!g) {
      g = { key, label, items: [] }
      groups.push(g)
    }
    g.items.push(t)
  }
  return groups
}

interface ComponentsEditorProps {
  utmZone: number
  polygonGeoJson: string | null
  components: ComponentEditorItem[]
  /**
   * Options that appear in the type selector. Pulled from the
   * /maestros/component-types API by the consumer, filtered by the
   * relevant categoryCode (explotacion vs beneficio).
   */
  availableTypes: ComponentTypeOption[]
  onAdd: (data: { componentTypeId: string; name?: string; northing: number; easting: number }) => Promise<ComponentEditorItem>
  onRemove: (componentId: string) => Promise<void>
  mapHeight?: string
  readOnly?: boolean
}

const addComponentSchema = z.object({
  componentTypeId: z.string().min(1, 'shared.componentsModal.zodTypeRequired'),
  name: z.string().optional(),
  northing: z.string().min(1, 'shared.componentsModal.zodNorthRequired').regex(/^\d+$/, 'shared.componentsModal.zodOnlyNumbers'),
  easting: z.string().min(1, 'shared.componentsModal.zodEastRequired').regex(/^\d+$/, 'shared.componentsModal.zodOnlyNumbers'),
})
type AddComponentFormData = z.infer<typeof addComponentSchema>

export const ComponentsEditor = ({
  utmZone,
  polygonGeoJson,
  components,
  availableTypes,
  onAdd,
  onRemove,
  mapHeight,
  readOnly,
}: ComponentsEditorProps) => {
  const c = useFormColors()
  const { t } = useT()
  const [adding, setAdding] = useState(false)
  const [focus, setFocus] = useState<{ lat: number; lng: number; nonce: number } | null>(null)

  // Click en una card → centra el mapa en el componente. Usado para el
  // emparejado lista ↔ marker numerado. El nonce fuerza el re-vuelo aunque se
  // clique la misma card varias veces.
  const flyToComponent = (lat: number, lng: number) => {
    setFocus((cur) => ({ lat, lng, nonce: (cur?.nonce ?? 0) + 1 }))
  }

  const defaultType = availableTypes[0]?.value ?? ''

  const formMethods = useForm<AddComponentFormData>({
    resolver: zodResolver(addComponentSchema),
    defaultValues: {
      componentTypeId: defaultType,
      name: '',
      northing: '',
      easting: '',
    },
  })
  const {
    register,
    control,
    handleSubmit,
    reset,
    setValue,
    getValues,
    formState: { errors },
  } = formMethods

  // availableTypes llega vacío al primer render (carga asíncrona del
  // catálogo /maestros/component-types). Cuando aparece, el <select> ya
  // muestra "Bocamina" como primera opción pero el form todavía tiene
  // componentTypeId="" y la validación zod min(1) revienta. Cuando el
  // valor del form esté vacío y exista un default disponible, lo
  // sincronizamos para que coincida con lo que el usuario ve.
  useEffect(() => {
    if (defaultType && !getValues('componentTypeId')) {
      setValue('componentTypeId', defaultType, { shouldDirty: false, shouldValidate: false })
    }
  }, [defaultType, setValue, getValues])

  const inputStyles = {
    bg: c.bgInput,
    border: '1px solid',
    borderColor: 'gray.200',
    borderRadius: 'lg',
    color: c.text,
    h: '40px',
    fontSize: 'sm',
    _placeholder: { color: c.textMuted },
    _focus: { outline: 'none', borderColor: '#b4532a', ring: '1px', ringColor: '#b4532a' },
  } as const

  const selectStyles: React.CSSProperties = {
    width: '100%',
    height: '40px',
    padding: '0 0.75rem',
    borderRadius: '0.5rem',
    border: '1px solid #e8dfd1',
    fontSize: '0.875rem',
    background: c.bgInput,
    color: c.text,
    outline: 'none',
  }

  const onSubmit = async (data: AddComponentFormData) => {
    const n = parseFloat(data.northing)
    const e = parseFloat(data.easting)

    setAdding(true)
    try {
      const result = await onAdd({
        componentTypeId: data.componentTypeId,
        name: data.name || undefined,
        northing: n,
        easting: e,
      })

      if (result.isOutsidePolygon) {
        toaster.warning({ description: t('shared.componentsModal.outsideToast') })
      }

      reset({ componentTypeId: defaultType, name: '', northing: '', easting: '' })
    } finally {
      setAdding(false)
    }
  }

  const handleRemove = async (componentId: string) => {
    await onRemove(componentId)
  }

  // Parse polygon for map. Validamos el RANGO WGS84 (no solo `Number.isFinite`):
  // una coordenada finita pero fuera de rango hace que Leaflet calcule límites
  // de tiles Infinity y se cuelgue en un bucle de tiles. Ver isValidLatLng.
  const polygonPositions: [number, number][] = []
  if (polygonGeoJson) {
    try {
      const geo = JSON.parse(polygonGeoJson)
      const coords = geo.type === 'Polygon' ? geo.coordinates[0] : []
      for (const coord of coords) {
        const lat = coord?.[1]
        const lng = coord?.[0]
        if (isValidLatLng(lat, lng)) {
          polygonPositions.push([lat, lng])
        }
      }
    } catch { /* ignore */ }
  }

  // Mantiene el número 1-based original del componente (idx+1) aunque algún
  // punto inválido se descarte, para que el marcador coincida con la card.
  const componentPositions = components
    .map((comp, idx) => {
      if (!Number.isFinite(comp.northing) || !Number.isFinite(comp.easting)) return null
      const { lat, lng } = utmToLatLng(comp.northing, comp.easting, utmZone)
      if (!isValidLatLng(lat, lng)) return null
      const typeName = comp.componentTypeName
        ?? availableTypes.find((t2) => t2.value === comp.componentTypeId)?.label
        ?? ''
      return { lat, lng, outside: comp.isOutsidePolygon, number: idx + 1, typeName, name: comp.name ?? null, code: comp.componentTypeCode ?? null }
    })
    .filter((p): p is { lat: number; lng: number; outside: boolean; number: number; typeName: string; name: string | null; code: string | null } => p !== null)

  const allPositions: [number, number][] = [
    ...polygonPositions,
    ...componentPositions.map((p) => [p.lat, p.lng] as [number, number]),
  ]

  const polygonLatLngs: LL[] = polygonPositions.map(([lat, lng]) => ({ lat, lng }))
  const allLatLngs: LL[] = allPositions.map(([lat, lng]) => ({ lat, lng }))

  return (
    <FormProvider {...formMethods}>
    <Grid templateColumns={{ base: '1fr', md: '1fr 1fr' }} gap={0}>
      {/* Left: Components list + form */}
      <VStack gap={4} alignItems="stretch" pr={{ base: 0, md: 4 }}>
        {!readOnly && (
          <Box bg={c.bgHover} borderRadius="xl" p={4}>
            <Text tid="shared.componentsModal.addTitle" fontSize="sm" fontWeight="700" color={c.text} mb={3} />
            <VStack gap={3} alignItems="stretch">
              <Grid templateColumns="1fr 1fr" gap={3}>
                <Box>
                  <Text tid="shared.componentsModal.typeLabel" fontSize="10px" fontWeight="600" color="#b5aa9d" textTransform="uppercase" mb={1} />
                  <Controller
                    name="componentTypeId"
                    control={control}
                    render={({ field }) => {
                      const selected = availableTypes.find((ct) => ct.value === field.value)
                      return (
                        <>
                          <select value={field.value} onChange={(e) => field.onChange(e.target.value)} style={selectStyles}>
                            {availableTypes.length === 0 && (
                              <option value="" disabled>{t('shared.componentsModal.zodTypeRequired')}</option>
                            )}
                            {groupTypesByTier(availableTypes).map((g) => (
                              <optgroup key={g.key} label={g.label}>
                                {g.items.map((ct) => (
                                  <option key={ct.value} value={ct.value} title={ct.description ?? undefined}>{ct.label}</option>
                                ))}
                              </optgroup>
                            ))}
                          </select>
                          {selected?.description && (
                            <Text fontSize="10px" color={c.textMuted} mt={1} lineHeight="1.3">{selected.description}</Text>
                          )}
                        </>
                      )
                    }}
                  />
                  {errors.componentTypeId && (
                    <Text fontSize="xs" color={c.error} mt={1}>{t(errors.componentTypeId.message ?? '')}</Text>
                  )}
                </Box>
                <Box>
                  <Text tid="shared.componentsModal.nameLabel" fontSize="10px" fontWeight="600" color="#b5aa9d" textTransform="uppercase" mb={1} />
                  <Input {...register('name')} placeholder={t('shared.componentsModal.namePlaceholder')} maxLength={200} {...inputStyles} />
                  <CharCounter name="name" max={200} />
                </Box>
              </Grid>
              <Grid templateColumns="1fr 1fr auto" gap={3} alignItems="flex-end">
                <Box>
                  <Text tid="shared.componentsModal.northLabel" fontSize="10px" fontWeight="600" color="#b5aa9d" textTransform="uppercase" mb={1} />
                  <Controller
                    name="northing"
                    control={control}
                    render={({ field }) => (
                      <Input
                        value={field.value}
                        onChange={(e) => field.onChange(e.target.value.replace(/[^0-9]/g, ''))}
                        placeholder={t('shared.componentsModal.northPlaceholder')}
                        inputMode="numeric"
                        fontFamily="monospace"
                        {...inputStyles}
                      />
                    )}
                  />
                  {errors.northing && (
                    <Text fontSize="xs" color={c.error} mt={1}>{t(errors.northing.message ?? '')}</Text>
                  )}
                </Box>
                <Box>
                  <Text tid="shared.componentsModal.eastLabel" fontSize="10px" fontWeight="600" color="#b5aa9d" textTransform="uppercase" mb={1} />
                  <Controller
                    name="easting"
                    control={control}
                    render={({ field }) => (
                      <Input
                        value={field.value}
                        onChange={(e) => field.onChange(e.target.value.replace(/[^0-9]/g, ''))}
                        placeholder={t('shared.componentsModal.eastPlaceholder')}
                        inputMode="numeric"
                        fontFamily="monospace"
                        {...inputStyles}
                      />
                    )}
                  />
                  {errors.easting && (
                    <Text fontSize="xs" color={c.error} mt={1}>{t(errors.easting.message ?? '')}</Text>
                  )}
                </Box>
                <Box
                  as="button" type="button" display="flex" alignItems="center" gap={2}
                  px={4} h="40px" bg="#b4532a" color="white" borderRadius="lg"
                  fontSize="sm" fontWeight="600" cursor={adding ? 'not-allowed' : 'pointer'}
                  opacity={adding ? 0.6 : 1} _hover={{ bg: '#085a30' }}
                  onClick={handleSubmit(onSubmit)} disabled={adding}
                >
                  {adding ? <Spinner size="sm" /> : <Plus size={16} />}
                </Box>
              </Grid>
            </VStack>
          </Box>
        )}

        <Text tid="shared.componentsModal.registeredCount" values={{ count: components.length }} fontSize="xs" fontWeight="700" color="#b5aa9d" textTransform="uppercase" letterSpacing="0.05em" />
        {components.length === 0 ? (
          <Box p={4} textAlign="center" border="1px dashed" borderColor="gray.200" borderRadius="xl">
            <Text tid="shared.componentsModal.emptyMessage" fontSize="sm" color={c.textMuted} />
          </Box>
        ) : (
          <VStack gap={2} alignItems="stretch">
            {components.map((comp, idx) => {
              const number = idx + 1
              const { lat, lng } = utmToLatLng(comp.northing, comp.easting, utmZone)
              const badgeBg = comp.isOutsidePolygon ? '#b23a26' : '#1d6664'
              return (
                <Flex
                  key={comp.id}
                  bg="white"
                  border="1px solid"
                  borderColor={comp.isOutsidePolygon ? '#fce4e4' : '#e8dfd1'}
                  borderRadius="lg"
                  p={3}
                  alignItems="center"
                  gap={3}
                  cursor="pointer"
                  _hover={{ borderColor: comp.isOutsidePolygon ? '#b23a26' : '#1d6664', bg: '#f8fafc' }}
                  transition="all 0.15s"
                  onClick={() => { if (isValidLatLng(lat, lng)) flyToComponent(lat, lng) }}
                  title={t('shared.componentsModal.cardClickHint')}
                >
                  {/* Numbered badge — matches the map marker label. */}
                  <Box
                    width="28px"
                    height="28px"
                    borderRadius="full"
                    bg={badgeBg}
                    color="white"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    fontSize="xs"
                    fontWeight="700"
                    flexShrink={0}
                  >
                    {number}
                  </Box>
                  <Box flex={1} minW={0}>
                    <Flex gap={2} alignItems="center">
                      <Text fontSize="sm" fontWeight="600" color={c.text}>{comp.componentTypeName}</Text>
                      {comp.isOutsidePolygon && (
                        <Flex alignItems="center" gap={1} color="#b23a26" fontSize="10px" fontWeight="600">
                          <AlertCircle size={12} /> {t('shared.componentsModal.outsidePolygon')}
                        </Flex>
                      )}
                    </Flex>
                    {comp.name && <Text fontSize="xs" color={c.textMuted}>{comp.name}</Text>}
                    <Text fontSize="xs" fontFamily="monospace" color={c.textMuted}>
                      N: {Math.round(comp.northing).toLocaleString('en')} | E: {Math.round(comp.easting).toLocaleString('en')}
                    </Text>
                  </Box>
                  {!readOnly && (
                    <Box
                      as="button"
                      type="button"
                      p={1}
                      borderRadius="md"
                      color="#b23a26"
                      cursor="pointer"
                      _hover={{ bg: '#fce4e4' }}
                      onClick={(e) => { e.stopPropagation(); handleRemove(comp.id) }}
                    >
                      <Trash2 size={14} />
                    </Box>
                  )}
                </Flex>
              )
            })}
          </VStack>
        )}
      </VStack>

      {/* Right: Map */}
      <Box
        borderRadius="xl"
        overflow="hidden"
        border="1px solid #e8dfd1"
        h={{ base: '250px', md: mapHeight ?? '450px' }}
        mt={{ base: 4, md: 0 }}
      >
        <KMap defaultCenter={{ lat: -12.0464, lng: -77.0428 }} defaultZoom={6} mapTypeId="hybrid">
          {polygonLatLngs.length >= 3 && <Polygon paths={polygonLatLngs} />}
          {componentPositions.map((pos) => (
            <AdvancedMarker
              key={pos.number}
              position={{ lat: pos.lat, lng: pos.lng }}
            >
              <MarkerTooltip title={pos.typeName} subtitle={pos.name}>
                <ComponentPin code={pos.code} badge={<NumberBadge label={pos.number} outside={pos.outside} />} />
              </MarkerTooltip>
            </AdvancedMarker>
          ))}
          {allLatLngs.length > 0 && <FitBounds points={allLatLngs} padding={30} nonce={allLatLngs.length} />}
          <FlyToFocus focus={focus} />
        </KMap>
      </Box>
    </Grid>
    </FormProvider>
  )
}

export default ComponentsEditor
